import { createTheme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

const theme = createTheme(
  {
    palette: {
      primary: {
        light: '#4dabf5',
        main: '#1976d2',
        dark: '#0d47a1',
        contrastText: '#ffffff',
      },
      secondary: {
        light: '#ff5131',
        main: '#d50000',
        dark: '#9b0000',
        contrastText: '#ffffff',
      },
      success: {
        light: '#81c784',
        main: '#4caf50',
        dark: '#388e3c',
        contrastText: '#ffffff',
      },
      info: {
        light: '#64b5f6',
        main: '#2196f3',
        dark: '#1976d2',
        contrastText: '#ffffff',
      },
      warning: {
        light: '#ffb74d',
        main: '#ff9800',
        dark: '#f57c00',
        contrastText: '#000000',
      },
      error: {
        light: '#e57373',
        main: '#f44336',
        dark: '#d32f2f',
        contrastText: '#ffffff',
      },
      background: {
        default: '#f5f7fa',
        paper: '#ffffff',
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      '0 2px 4px rgba(0,0,0,0.05)',
      '0 4px 8px rgba(0,0,0,0.08)',
      '0 6px 12px rgba(0,0,0,0.1)',
      '0 8px 16px rgba(0,0,0,0.12)',
      '0 10px 20px rgba(0,0,0,0.14)',
      '0 12px 24px rgba(0,0,0,0.16)',
      '0 14px 28px rgba(0,0,0,0.18)',
      '0 16px 32px rgba(0,0,0,0.2)',
      '0 2px 8px rgba(0,118,255,0.2)',
      '0 4px 12px rgba(0,118,255,0.25)',
      '0 6px 16px rgba(0,118,255,0.3)',
      '0 8px 20px rgba(0,118,255,0.35)',
      '0 10px 24px rgba(0,118,255,0.4)',
      '0 12px 28px rgba(0,118,255,0.45)',
      '0 14px 32px rgba(0,118,255,0.5)',
      '0 16px 36px rgba(0,118,255,0.55)',
      '0 18px 40px rgba(0,118,255,0.6)',
      '0 20px 44px rgba(0,118,255,0.65)',
      '0 22px 48px rgba(0,118,255,0.7)',
      '0 24px 52px rgba(0,118,255,0.75)',
      '0 26px 56px rgba(0,118,255,0.8)',
      '0 28px 60px rgba(0,118,255,0.85)',
      '0 30px 64px rgba(0,118,255,0.9)',
      '0 32px 68px rgba(0,118,255,0.95)',
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            padding: '8px 20px',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
          contained: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transform: 'translateY(-2px)',
            },
          },
          sizeLarge: {
            padding: '12px 28px',
            fontSize: '1rem',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          },
          elevation2: {
            boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
          },
          elevation3: {
            boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'all 0.2s ease',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 3px rgba(25,118,210,0.1)',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            backgroundColor: '#f5f7fa',
            color: '#212121',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
          },
        },
      },
    },
  },
  esES
);

export default theme;
